/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  message,
} from 'antd';
import moment from 'moment';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';

import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getValoresDeCalculo,
  postValorDeCalculo,
  deleteValorDeCalculo,
  patchValorDeCalculo,
  getValorDeCalculo,
  autorizarValorDeCalculo,
} from 'api/catalogos/ValorCalculo';
import { checkToken } from 'api/utils';
// Patrón genérico, puedes ajustar si necesitas uno específico
import { NOMBRES_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getLiquidacionesUnidades } from 'api/nominas/liquidaciones/tipoLiquidacionUnidad';

export const valorDeCalculoPermission = {
  permissionModel: 'valordecalculo',
  catalogsModels: [],
  tabsModels: [],
};

const ValorDeCalculo = ({ permission, liquidacionId }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposLiquidacion, setTiposLiquidacion] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    if (!liquidacionId) return; // Si no hay ID de padre, no cargamos nada
    setLoading(true);
    // eslint-disable-next-line max-len
    const _data = await getValoresDeCalculo({ liquidacion_configuracion_percepcion: liquidacionId });
    setData(formatReceived(_data));
    setLoading(false);
  };
  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _tiposLiquidacion,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getLiquidacionesUnidades(),
        getValoresDeCalculo({ liquidacion_configuracion_percepcion: liquidacionId }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposLiquidacion(_tiposLiquidacion);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [liquidacionId]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const item = data.find((d) => d.id === key);
    const _fullData = await getValorDeCalculo(item.id); // Get detalle completo

    if (_fullData) {
      form.resetFields();
      const formatted = {
        ...formatReceived(_fullData),
        vigencia_final: _fullData.vigencia_final ? moment(_fullData.vigencia_final) : null,
      };
      form.setFieldsValue(formatted);
      setVisible(true);
    }
    setLoading(false);
  };

  const onFinish = async (_values) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      // INYECTAMOS EL ID DEL PADRE AUTOMÁTICAMENTE
      values.liquidacion_configuracion_percepcion = liquidacionId;

      if (values.vigencia_final) {
        values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');
      }

      const [key] = selectedRowKeys;
      const saved = await (key ? patchValorDeCalculo(key, values) : postValorDeCalculo(values));
      if (saved) {
        setVisible(false);
        form.resetFields();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };
  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteValorDeCalculo(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar sin selección');
        return;
      }
      const saved = await autorizarValorDeCalculo(key);
      if (saved) {
        const _data = await getValoresDeCalculo();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Unidad de Liquidación',
      dataIndex: 'tipo_de_liquidacion_unidad',
      key: 'tipo_de_liquidacion_unidad',
      width: 300,
      render: (val) => tiposLiquidacion.find((unidades) => unidades.id === val)?.descripcion,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    descripcion: [
      requiredRule,
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'valordecalculo', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={{
            hasAllViewPermission: true,
            add: true,
            change: true,
            delete: true,
          }}
          hasAllViewPermission
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Valor de Cálculo`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
          width={600}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_de_liquidacion_unidad"
                label="Unidad de Liquidación"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={tiposLiquidacion}
                  disabled={disableFields}
                  placeholder="Seleccione una unidad"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.descripcion}
                label="Descripción"
                hasFeedback
              >
                <Input
                  allowClear
                  disabled={disableFields}
                  maxLength={255}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
                hasFeedback
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabled={disableFields}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Valor de Cálculo: ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar Valor de Cálculo: ${selectedData?.descripcion}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar Valor de Cálculo: ${selectedData?.descripcion}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ValorDeCalculo;
